import { useCallback, useEffect, useRef, useState } from "react";

import { useSession } from "../../session/SessionContext.js";
import { AvatarField } from "./AvatarField.js";

/**
 * An agent's own account.
 *
 * Behind no permission at all, unlike everything else in this area: changing your
 * own password is not an administrative act, and gating it would leave a colleague
 * with a narrow role unable to do the one thing every account holder must be able
 * to do. The API enforces the same rule — the id comes from the session, so there
 * is nothing here that could edit somebody else.
 *
 * The photo matters beyond vanity. It is what a customer sees on every message this
 * agent sends, resolved live, so a change here reaches conversations that are
 * already open.
 */

interface Profile {
  readonly agentId: string;
  readonly email: string;
  readonly name: string;
  readonly role: string;
  readonly avatarUrl?: string;
}

export function ProfilePanel() {
  const { api } = useSession();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [detailsState, setDetailsState] = useState<"idle" | "saving" | "saved">("idle");
  const [detailsError, setDetailsError] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordState, setPasswordState] = useState<"idle" | "saving" | "saved">("idle");
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [loadError, setLoadError] = useState<string | null>(null);

  /**
   * Cleared after a moment so "Saved" does not sit there for the rest of the
   * session, implying the next edit has also been saved.
   */
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (savedTimer.current !== null) clearTimeout(savedTimer.current);
    },
    [],
  );

  const adopt = useCallback((next: Profile) => {
    setProfile(next);
    setName(next.name);
    setEmail(next.email);
  }, []);

  useEffect(() => {
    let live = true;

    void api
      .get<Profile>("/support/profile")
      .then((loaded) => {
        if (live) adopt(loaded);
      })
      .catch(() => {
        if (live) setLoadError("Your account could not be loaded.");
      });

    return () => {
      live = false;
    };
  }, [api, adopt]);

  function flashSaved(set: (state: "idle" | "saving" | "saved") => void): void {
    set("saved");

    if (savedTimer.current !== null) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => {
      set("idle");
    }, 2_500);
  }

  async function saveDetails(): Promise<void> {
    setDetailsError(null);
    setDetailsState("saving");

    try {
      const updated = await api.patch<Profile>("/support/profile", { name, email });
      adopt(updated);
      flashSaved(setDetailsState);
    } catch (cause) {
      setDetailsState("idle");
      setDetailsError(messageFor(cause, "Your details could not be saved."));
    }
  }

  async function savePassword(): Promise<void> {
    setPasswordError(null);
    setPasswordState("saving");

    try {
      await api.post("/support/profile/password", { currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      flashSaved(setPasswordState);
    } catch (cause) {
      setPasswordState("idle");
      setPasswordError(messageFor(cause, "Your password could not be changed."));
    }
  }

  if (loadError !== null) return <p className="error">{loadError}</p>;
  if (profile === null) return <p className="muted">Loading…</p>;

  const detailsDirty = name !== profile.name || email !== profile.email;

  return (
    <div className="admin-form">
      <section className="card">
        <h2>Your photo</h2>
        <p className="hint">
          Customers see this on every message you send, in the chat window and in the app. Changing
          it updates conversations that are already open.
        </p>

        <AvatarField
          name={profile.name}
          avatarUrl={profile.avatarUrl}
          uploadPath="/support/profile/avatar"
          onChanged={(updated) => {
            adopt(updated as Profile);
          }}
        />
      </section>

      <section className="card">
        <h2>Your details</h2>

        <label>
          Name
          <input
            value={name}
            maxLength={80}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          <span className="hint">
            What a customer is told when you join a conversation — &ldquo;
            {name.trim() || "Your name"} joined the chat&rdquo;.
          </span>
        </label>

        <label>
          Email
          <input
            type="email"
            value={email}
            maxLength={254}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <span className="hint">You sign in with this. Customers never see it.</span>
        </label>

        {detailsError !== null && <p className="error">{detailsError}</p>}

        <div className="row">
          <button
            className="primary"
            disabled={!detailsDirty || detailsState === "saving" || name.trim() === ""}
            onClick={() => void saveDetails()}
          >
            {detailsState === "saving" ? "Saving…" : "Save details"}
          </button>
          {detailsState === "saved" && <span className="small muted">Saved.</span>}
        </div>
      </section>

      <section className="card">
        <h2>Change your password</h2>
        <p className="hint">
          Your current password is required. Without it, anyone who found this screen already signed
          in could lock you out of your own account.
        </p>

        <label>
          Current password
          <input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => {
              setCurrentPassword(event.target.value);
            }}
          />
        </label>

        <label>
          New password
          <input
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => {
              setNewPassword(event.target.value);
            }}
          />
          <span className="hint">At least 12 characters. Length matters more than symbols.</span>
        </label>

        {passwordError !== null && <p className="error">{passwordError}</p>}

        <div className="row">
          <button
            className="primary"
            disabled={
              passwordState === "saving" || currentPassword === "" || newPassword.length < 12
            }
            onClick={() => void savePassword()}
          >
            {passwordState === "saving" ? "Changing…" : "Change password"}
          </button>
          {passwordState === "saved" && <span className="small muted">Password changed.</span>}
        </div>
      </section>
    </div>
  );
}

/**
 * The server's own message when there is one.
 *
 * "That email address is already in use" is worth showing verbatim; a network
 * failure is not, and the fallback covers it.
 */
export function messageFor(cause: unknown, fallback: string): string {
  if (cause instanceof Error && cause.message !== "") return cause.message;

  return fallback;
}
